import { useCallback, useEffect, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  ConnectionLineType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { NodeToolbar } from './NodeToolbar';
import { NodeSettingsPanel } from './NodeSettingsPanel';
import { WorkflowNode } from './nodes/WorkflowNode';
import { useParams } from 'react-router-dom';
import workflowService from '../../services/workflowService';
import { useQuery } from '@tanstack/react-query';
import { use } from 'react';

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: {
    stroke: 'hsl(45, 93%, 58%)',
    strokeWidth: 2,
  },

  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: 'hsl(45, 93%, 58%)',
    width: 20,
    height: 20,
  },
};

const nodeTypes = {
  workflowNode: WorkflowNode,
};

const initialNodes = [
  {
    id: 'start-1',
    type: 'workflowNode',
    position: { x: 250, y: 100 },
    data: { label: 'Start', type: 'start' },
  },
];

export const WorkflowCanvas = () => {
  const { id } = useParams();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  const getAllWorkflows = useQuery({
    queryKey: ['get-workflow-details'],
    queryFn: () => workflowService.getWorkflowDetail(id),
    staleTime: Infinity,
  });

  const generateNodesAndEdgesFromSteps = steps => {
    const nodes = [];
    const edges = [];

    // 1️⃣ Add start node
    const startNodeId = 'start-1';
    nodes.push({
      id: startNodeId,
      type: 'workflowNode',
      position: { x: 250, y: 100 },
      data: { label: 'Start', type: 'start' },
    });

    // Previous node (initially Start)
    let previousNodeId = startNodeId;

    // 2️⃣ Loop through each step and create nodes + edges
    steps.forEach((step, index) => {
      const nodeId = `${step.type}-${Date.now()}-${index}`;

      // Create node
      nodes.push({
        id: nodeId,
        type: 'workflowNode',
        position: { x: 250, y: 200 + index * 130 },
        data: {
          label: step.step_name,
          type: step.type,
          ...step,
        },
      });

      // Create edge from previous → current
      edges.push({
        id: `edge-${previousNodeId}-${nodeId}`,
        source: previousNodeId,
        target: nodeId,
        ...defaultEdgeOptions,
      });

      previousNodeId = nodeId;
    });

    return { nodes, edges };
  };

  const onConnect = useCallback(
    params => {
      const newEdge = {
        ...params,
        ...defaultEdgeOptions,
      };

      setEdges(eds => addEdge(newEdge, eds));
    },
    [setEdges],
  );

  const onNodeClick = useCallback((_event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const addNode = useCallback(
    type => {
      const newNode = {
        id: `${type}-${Date.now()}`,
        type: 'workflowNode',
        position: {
          x: Math.random() * 400 + 100,
          y: Math.random() * 400 + 100,
        },
        data: { label: type.charAt(0).toUpperCase() + type.slice(1), type },
      };

      setNodes(nds => [...nds, newNode]);
    },
    [setNodes],
  );

  const updateNodeData = useCallback(
    (nodeId, newData) => {
      setNodes(nds => nds.map(node => (node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node)));
    },
    [setNodes],
  );

  useEffect(() => {
    if (getAllWorkflows?.data?.detail?.steps) {
      const { nodes: generatedNodes, edges: generatedEdges } = generateNodesAndEdgesFromSteps(
        getAllWorkflows.data.detail.steps,
      );

      setNodes(generatedNodes);
      setEdges(generatedEdges);
    }
  }, [getAllWorkflows?.data]);

  useEffect(() => {
    getAllWorkflows?.refetch();
  }, []);

  return (
    <div className="flex-1 flex">
      <div className="flex-1 relative">
        {getAllWorkflows?.data?.detail?.name && (
          <div className="absolute bottom-4 right-4 z-10">
            <div className="bg-[var(--card-bg)] border border-[rgba(157,157,157,0.3)] rounded-lg p-3 shadow-lg">
              <h2
                className="text-lg font-semibold text-foreground mb-0 text-ink"
                style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getAllWorkflows?.data?.detail?.name}
              </h2>
            </div>
          </div>
        )}

        <NodeToolbar
          onAddNode={addNode}
          setSelectedNode={setSelectedNode}
          nodesData={nodes}
          edgesData={edges}
          workflowId={id}
          isEdit={getAllWorkflows?.data?.detail}
        />

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          connectionLineType={ConnectionLineType.SmoothStep}
          connectionLineStyle={{
            stroke: 'hsl(45, 93%, 58%)',
            strokeWidth: 2,
          }}
          fitView
          className="bg-[var(--card-bg)]">
          <Background color="var(--card-bg)" gap={16} />
        </ReactFlow>
      </div>

      {selectedNode && selectedNode?.data?.type !== 'start' && (
        <NodeSettingsPanel node={selectedNode} onUpdateNode={updateNodeData} onClose={() => setSelectedNode(null)} />
      )}
    </div>
  );
};
